import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { Advance, Compensation, CategoryItem } from "@/lib/types";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, HandCoins, Download, Trash2, Undo2 } from "lucide-react";
import { DateEditInline } from "@/components/DateEditInline";
import { BulkCompensateDialog } from "@/components/BulkCompensateDialog";

const AdvancesPage: React.FC = () => {
  const [advances, setAdvances] = useState<Advance[]>([]);
  const [types, setTypes] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Advance | null>(null);
  const [comps, setComps] = useState<Compensation[]>([]);
  const [form, setForm] = useState({ counterpart: "", advance_type: "prestamo_socio", amount: "", note: "", date: "" });
  const [comp, setComp] = useState({ amount: "", method: "descuento", note: "", date: "" });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState<"pending" | "compensated" | "all">("pending");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const togglePick = (id: string) => {
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const load = async () => {
    setLoading(true);
    const res = await client.get("/advances");
    setAdvances(res.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    client.get("/advances/types").then((r) => setTypes(r.data.types));
  }, []);

  const typeLabel = (k: string) => types.find((t) => t.key === k)?.label || k;

  const openAdvance = async (a: Advance) => {
    setSelected(a);
    setComp({ amount: "", method: "descuento", note: "", date: (a.date || "").slice(0, 10) });
    const res = await client.get(`/advances/${a.id}/compensations`);
    setComps(res.data);
  };

  const createAdvance = async () => {
    if (!form.counterpart || !form.amount) {
      toast.error("Contraparte y monto son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await client.post("/advances", {
        counterpart: form.counterpart,
        advance_type: form.advance_type,
        amount: parseFloat(form.amount),
        note: form.note,
        date: form.date ? `${form.date}T12:00:00Z` : undefined,
      });
      toast.success("Anticipo registrado");
      setCreateOpen(false);
      setForm({ counterpart: "", advance_type: "prestamo_socio", amount: "", note: "", date: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const compensate = async () => {
    if (!selected) return;
    const amount = parseFloat(comp.amount);
    if (!amount || amount <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    setSaving(true);
    try {
      await client.post(`/advances/${selected.id}/compensate`, {
        amount,
        method: comp.method,
        note: comp.note,
        date: comp.date ? `${comp.date}T12:00:00Z` : undefined,
      });
      toast.success("Compensación registrada");
      setComp({ amount: "", method: "descuento", note: "", date: comp.date });
      const [a, c] = await Promise.all([client.get(`/advances/${selected.id}`), client.get(`/advances/${selected.id}/compensations`)]);
      setSelected(a.data);
      setComps(c.data);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const undoCompensation = async (c: Compensation) => {
    if (!selected) return;
    if (!window.confirm(`¿Deshacer la compensación de ${money(c.amount)}? Se quitará su movimiento de Caja y el anticipo volverá a quedar pendiente.`)) return;
    try {
      await client.delete(`/advances/${selected.id}/compensations/${c.id}`);
      toast.success("Compensación deshecha");
      const [a, r] = await Promise.all([client.get(`/advances/${selected.id}`), client.get(`/advances/${selected.id}/compensations`)]);
      setSelected(a.data);
      setComps(r.data);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const totalOutstanding = advances.reduce((s, a) => s + (a.status !== "compensated" ? a.outstanding : 0), 0);
  const pendingCount = advances.filter((a) => a.status !== "compensated").length;
  const compCount = advances.filter((a) => a.status === "compensated").length;
  const filtered = advances.filter((a) =>
    filter === "all" ? true : filter === "compensated" ? a.status === "compensated" : a.status !== "compensated"
  );

  const pickable = filtered.filter((a) => a.status !== "compensated");
  const pickedAdvances = advances.filter((a) => picked.has(a.id) && a.status !== "compensated");
  const allPicked = pickable.length > 0 && pickable.every((a) => picked.has(a.id));

  const importFromCash = async () => {
    if (!window.confirm("Se importarán al módulo de Anticipos los movimientos de Caja con categoría de anticipo o préstamo que registraste antes. Los anticipos de empleado se reclasificarán como 'por aplicar a nómina'. ¿Continuar?")) return;
    setImporting(true);
    try {
      const res = await client.post("/advances/backfill");
      const { created, reclassified } = res.data;
      toast.success(`Importados ${created} anticipo(s) · ${reclassified} reclasificado(s) a nómina`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="advances-page">
      <PageHeader
        title="Anticipos y préstamos"
        subtitle={`Pendiente de compensar: ${money(totalOutstanding)}`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={importFromCash}
              disabled={importing}
              data-testid="import-advances-button"
              className="flex items-center gap-2 rounded-none border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={2} />}
              Importar de Caja
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              data-testid="create-advance-button"
              className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000]"
            >
              <Plus className="h-4 w-4" strokeWidth={2} />
              Registrar anticipo
            </button>
          </div>
        }
      />

      <div className="border border-slate-200 bg-white">
        <div className="flex items-center gap-1 border-b border-slate-200 p-3" data-testid="advances-filter">
          {([
            { k: "pending", l: `Pendientes (${pendingCount})` },
            { k: "compensated", l: `Compensados (${compCount})` },
            { k: "all", l: `Todos (${advances.length})` },
          ] as const).map((o) => (
            <button
              key={o.k}
              onClick={() => setFilter(o.k)}
              data-testid={`advances-filter-${o.k}`}
              className={`rounded-none px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === o.k ? "bg-[#DD0000] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {o.l}
            </button>
          ))}
          {pickedAdvances.length > 0 && (
            <div className="ml-auto flex items-center gap-3" data-testid="bulk-selection-bar">
              <span className="text-xs text-slate-600">
                {pickedAdvances.length} seleccionado(s) ·{" "}
                <span className="font-medium tabular text-slate-900">{money(pickedAdvances.reduce((s, a) => s + a.outstanding, 0))}</span>
              </span>
              <button onClick={() => setPicked(new Set())} data-testid="bulk-clear" className="text-xs text-slate-400 hover:text-slate-600">
                Limpiar
              </button>
              <button
                onClick={() => setBulkOpen(true)}
                data-testid="bulk-compensate-button"
                className="rounded-none bg-[#DD0000] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#B00000]"
              >
                Compensar seleccionados
              </button>
            </div>
          )}
        </div>
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState message={advances.length === 0 ? "No hay anticipos registrados" : "No hay anticipos en este filtro"} testid="advances-empty" />
        ) : (
          <table className="w-full text-sm" data-testid="advances-table">
            <thead>
              <tr className="bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="w-10 py-3 pl-5">
                  {pickable.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allPicked}
                      onChange={() => setPicked(allPicked ? new Set() : new Set(pickable.map((a) => a.id)))}
                      data-testid="advances-pick-all"
                      className="h-4 w-4 accent-[#DD0000]"
                    />
                  )}
                </th>
                <th className="px-5 py-3">Contraparte</th>
                <th className="px-5 py-3">Tipo</th>
                <th className="px-5 py-3">Fecha</th>
                <th className="px-5 py-3 text-right">Monto</th>
                <th className="px-5 py-3 text-right">Compensado</th>
                <th className="px-5 py-3 text-right">Pendiente</th>
                <th className="px-5 py-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => openAdvance(a)}
                  className="cursor-pointer border-b border-slate-50 hover:bg-slate-50/50"
                  data-testid={`advance-row-${a.id}`}
                >
                  <td className="w-10 py-3 pl-5" onClick={(e) => e.stopPropagation()}>
                    {a.status !== "compensated" && (
                      <input
                        type="checkbox"
                        checked={picked.has(a.id)}
                        onChange={() => togglePick(a.id)}
                        data-testid={`advance-pick-${a.id}`}
                        className="h-4 w-4 accent-[#DD0000]"
                      />
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 font-medium text-slate-800">
                      <HandCoins className="h-4 w-4 text-violet-600" strokeWidth={1.5} />
                      {a.counterpart}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{typeLabel(a.advance_type)}</td>
                  <td className="px-5 py-3 text-slate-500">{fmtDate(a.date)}</td>
                  <td className="px-5 py-3 text-right tabular text-slate-700">{money(a.amount)}</td>
                  <td className="px-5 py-3 text-right tabular text-emerald-600">{money(a.compensated)}</td>
                  <td className="px-5 py-3 text-right font-medium tabular text-slate-900">{money(a.outstanding)}</td>
                  <td className="px-5 py-3">
                    <StatusBadge
                      status={a.status === "compensated" ? "paid" : "active"}
                      label={a.status === "compensated" ? "Compensado" : a.status === "partial" ? "Parcial" : "Pendiente"}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <BulkCompensateDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        advances={pickedAdvances}
        onDone={() => { setPicked(new Set()); load(); }}
      />

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none" data-testid="create-advance-dialog">
          <DialogHeader>
            <DialogTitle>Registrar anticipo / préstamo</DialogTitle>
            <DialogDescription>Sale de caja pero no es gasto ni retiro. Los anticipos de sueldo y préstamos a empleados quedan "por aplicar a nómina" y los compensas el día de pago.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Contraparte (socio / empleado)</Label>
              <Input
                value={form.counterpart}
                onChange={(e) => setForm({ ...form, counterpart: e.target.value })}
                data-testid="advance-counterpart-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Tipo</Label>
              <Select value={form.advance_type} onValueChange={(v) => setForm({ ...form, advance_type: v })}>
                <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="advance-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Monto (MXN)</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="advance-amount-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Fecha</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="advance-date-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Nota</Label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="advance-note-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={createAdvance}
              disabled={saving}
              data-testid="advance-submit"
              className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Registrar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail + compensate panel */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto rounded-none border-l border-slate-200 p-0 sm:max-w-[440px]" data-testid="advance-detail-panel">
          <SheetHeader className="border-b border-slate-100 p-6">
            <SheetTitle className="text-lg font-bold tracking-tight">{selected?.counterpart}</SheetTitle>
            <SheetDescription>Compensa el anticipo contra un pago o sueldo posterior.</SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="space-y-6 p-6">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-slate-200 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Monto</div>
                  <div className="mt-1 text-sm font-medium tabular text-slate-900">{money(selected.amount)}</div>
                </div>
                <div className="border border-slate-200 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Compensado</div>
                  <div className="mt-1 text-sm font-medium tabular text-emerald-600">{money(selected.compensated)}</div>
                </div>
                <div className="border border-slate-200 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pendiente</div>
                  <div className="mt-1 text-sm font-medium tabular text-slate-900">{money(selected.outstanding)}</div>
                </div>
              </div>

              {selected.compensated === 0 && (
                <button
                  onClick={async () => {
                    if (!window.confirm("¿Eliminar este anticipo? También se eliminará su movimiento en Caja (si existe).")) return;
                    try {
                      await client.delete(`/advances/${selected.id}`);
                      toast.success("Anticipo eliminado");
                      setSelected(null);
                      load();
                    } catch (err) {
                      toast.error(apiError(err));
                    }
                  }}
                  data-testid="advance-delete-button"
                  className="flex items-center gap-1.5 text-xs text-slate-400 transition-colors hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar anticipo (duplicado o error)
                </button>
              )}

              <div className="border border-slate-200 p-4">
                <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Compensar</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Monto a compensar</Label>
                    <Input
                      type="number"
                      value={comp.amount}
                      onChange={(e) => setComp({ ...comp, amount: e.target.value })}
                      data-testid="compensate-amount"
                      className="mt-1 rounded-none border-slate-300"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Fecha del movimiento</Label>
                    <Input
                      type="date"
                      value={comp.date}
                      onChange={(e) => setComp({ ...comp, date: e.target.value })}
                      data-testid="compensate-date"
                      className="mt-1 rounded-none border-slate-300"
                    />
                    <p className="mt-1 text-[10px] text-slate-400">El gasto de nómina se registrará en esta fecha (por defecto, la del anticipo).</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Método</Label>
                    <Select value={comp.method} onValueChange={(v) => setComp({ ...comp, method: v })}>
                      <SelectTrigger className="mt-1 rounded-none border-slate-300" data-testid="compensate-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="descuento">Nómina (descuento contra sueldo / pago)</SelectItem>
                        <SelectItem value="deposito">Reingreso (depósito de efectivo a caja)</SelectItem>
                        <SelectItem value="utilidades">Utilidades de socio (retiro de utilidades)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {comp.method === "descuento" && (
                    <p className="text-[11px] leading-relaxed text-slate-500" data-testid="compensate-payroll-hint">
                      Se registrará como gasto de <strong>Nómina / Sueldos</strong> (gasto operativo) y el anticipo quedará saldado, sin volver a salir dinero de caja.
                    </p>
                  )}
                  {comp.method === "utilidades" && (
                    <p className="text-[11px] leading-relaxed text-slate-500" data-testid="compensate-profit-hint">
                      Se registrará como <strong>Retiro de utilidades</strong> del socio y el anticipo quedará saldado, sin volver a salir dinero de caja.
                    </p>
                  )}
                  {comp.method === "deposito" && (
                    <p className="text-[11px] leading-relaxed text-slate-500" data-testid="compensate-deposit-hint">
                      Se registrará una <strong>entrada</strong> en Caja por el dinero devuelto.
                    </p>
                  )}
                  <div>
                    <Label className="text-xs text-slate-500">Nota</Label>
                    <Textarea
                      value={comp.note}
                      onChange={(e) => setComp({ ...comp, note: e.target.value })}
                      data-testid="compensate-note"
                      rows={2}
                      className="mt-1 rounded-none border-slate-300"
                    />
                  </div>
                  <button
                    onClick={compensate}
                    disabled={saving || selected.status === "compensated"}
                    data-testid="compensate-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-none bg-[#DD0000] py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {selected.status === "compensated" ? "Totalmente compensado" : "Registrar compensación"}
                  </button>
                </div>
              </div>

              <div>
                <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Compensaciones</h4>
                {comps.length === 0 ? (
                  <p className="text-xs text-slate-400">Sin compensaciones aún.</p>
                ) : (
                  <div className="space-y-2" data-testid="compensations-list">
                    {comps.map((c) => (
                      <div key={c.id} className="border border-slate-100 bg-slate-50/50 p-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-medium tabular text-slate-800">{money(c.amount)}</span>
                          <DateEditInline
                            date={c.date}
                            testid={`comp-date-${c.id}`}
                            onSave={async (d) => {
                              await client.patch(`/advances/${selected.id}/compensations/${c.id}`, { date: d });
                              const r = await client.get(`/advances/${selected.id}/compensations`);
                              setComps(r.data);
                            }}
                          />
                        </div>
                        <div className="mt-1 text-slate-500">
                          {c.method === "deposito" ? "Reingreso" : c.method === "utilidades" ? "Utilidades de socio" : "Nómina / descuento"}
                          {c.note ? ` · "${c.note}"` : ""}
                        </div>
                        <button
                          onClick={() => undoCompensation(c)}
                          data-testid={`comp-undo-${c.id}`}
                          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#DD0000] transition-colors hover:text-[#B00000]"
                        >
                          <Undo2 className="h-3 w-3" /> Deshacer compensación
                        </button>
                        {c.has_movement === false && c.method !== "deposito" && (
                          <div className="mt-2 border-l-2 border-amber-400 bg-amber-50 p-2" data-testid={`comp-missing-${c.id}`}>
                            <p className="text-[11px] text-amber-800">Esta compensación no generó movimiento en Caja. ¿A qué la aplicas?</p>
                            <div className="mt-1.5 flex gap-2">
                              {([["nomina", "Aplicar a nómina"], ["utilidades", "Aplicar a utilidades"]] as const).map(([t, label]) => (
                                <button
                                  key={t}
                                  onClick={async () => {
                                    try {
                                      await client.post(`/advances/${selected.id}/compensations/${c.id}/apply`, { target: t });
                                      toast.success("Movimiento registrado en Caja");
                                      const r = await client.get(`/advances/${selected.id}/compensations`);
                                      setComps(r.data);
                                    } catch (err) {
                                      toast.error(apiError(err));
                                    }
                                  }}
                                  data-testid={`comp-apply-${t}-${c.id}`}
                                  className="border border-amber-400 bg-white px-2 py-1 text-[11px] font-medium text-amber-800 transition-colors hover:bg-amber-100"
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdvancesPage;
